import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemSecondaryAction, CircularProgress, Alert
} from '@mui/material';
import { WalletStyles } from '../styles';
import { homenetwork, compState } from '../../../Headerr';
import { bsv } from '../../../scrypt-ts-this/distthis/index';

interface Account {
  label: string;
  encryptedKey: string;
  iv: string;
  salt: string;
}

const STORAGE_KEY = 'bvm_wallet_accounts';
const ACTIVE_KEY = 'bvm_wallet_active_account';

async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']
  );
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as any, iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptData(data: string, password: string): Promise<{ encrypted: string; iv: string; salt: string }> {
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const enc = new TextEncoder();
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(data)
  );
  return {
    encrypted: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
    iv: btoa(String.fromCharCode(...iv)),
    salt: btoa(String.fromCharCode(...salt)),
  };
}

async function decryptData(encryptedB64: string, ivB64: string, saltB64: string, password: string): Promise<string> {
  const salt = Uint8Array.from(atob(saltB64), c => c.charCodeAt(0));
  const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
  const encrypted = Uint8Array.from(atob(encryptedB64), c => c.charCodeAt(0));
  const key = await deriveKey(password, salt);
  const decrypted = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    encrypted
  );
  return new TextDecoder().decode(decrypted);
}

const WalletAccounts: FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [masterPassword, setMasterPassword] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [decryptedKey, setDecryptedKey] = useState('');

  const loadAccounts = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setAccounts(JSON.parse(stored));
      }
      const active = localStorage.getItem(ACTIVE_KEY);
      if (active) {
        setActiveIndex(parseInt(active, 10));
      }
    } catch (e) {
      console.error('Failed to load accounts:', e);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const saveAccounts = (accs: Account[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accs));
    setAccounts(accs);
  };

  const handleUnlock = async () => {
    if (!masterPassword || masterPassword.length < 8) {
      setError('Master password deve ter pelo menos 8 caracteres.');
      return;
    }
    // Try to decrypt the first account to verify password
    if (accounts.length > 0) {
      try {
        await decryptData(accounts[0].encryptedKey, accounts[0].iv, accounts[0].salt, masterPassword);
        setUnlocked(true);
        setError('');
      } catch (e) {
        setError('Senha incorreta.');
      }
    } else {
      setUnlocked(true);
      setError('');
    }
  };

  const handleAddAccount = async () => {
    if (!newLabel || !newKey) {
      setError('Preencha o label e a chave privada/mnemônico.');
      return;
    }
    setSaving(true);
    try {
      const { encrypted, iv, salt } = await encryptData(newKey, masterPassword);
      const newAccount: Account = { label: newLabel, encryptedKey: encrypted, iv, salt };
      const updated = [...accounts, newAccount];
      saveAccounts(updated);
      setNewLabel('');
      setNewKey('');
      setAddOpen(false);
      setError('');
    } catch (e: any) {
      setError(`Erro ao salvar: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAccount = async (idx: number) => {
    setActiveIndex(idx);
    localStorage.setItem(ACTIVE_KEY, String(idx));
    try {
      const acc = accounts[idx];
      const key = await decryptData(acc.encryptedKey, acc.iv, acc.salt, masterPassword);
      setDecryptedKey(key);
      // Optionally could set homepvtKey here
    } catch (e) {
      setError('Falha ao descriptografar a conta.');
    }
  };

  const handleDeleteAccount = (idx: number) => {
    const updated = accounts.filter((_, i) => i !== idx);
    saveAccounts(updated);
    if (activeIndex >= updated.length) {
      setActiveIndex(Math.max(0, updated.length - 1));
    }
  };

  if (!unlocked) {
    return (
      <Box sx={WalletStyles.card}>
        <Typography sx={WalletStyles.sectionTitle}>Gerenciador de Contas</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(123, 162, 56, 0.7)', mb: 2 }}>
          Insira a master password para desbloquear suas contas.
        </Typography>
        <TextField
          label="Master Password"
          type="password"
          value={masterPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setMasterPassword(e.target.value)}
          fullWidth
          variant="outlined"
          sx={{ ...WalletStyles.inputField, mb: 2 }}
        />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button variant="contained" onClick={handleUnlock} sx={WalletStyles.actionButton}>
          🔓 Desbloquear
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={WalletStyles.card}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={WalletStyles.sectionTitle}>Contas ({accounts.length})</Typography>
          <Button variant="outlined" size="small" onClick={() => setAddOpen(true)} sx={WalletStyles.outlinedButton}>
            ➕ Nova Conta
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {accounts.length === 0 ? (
          <Typography sx={WalletStyles.noDataText}>
            Nenhuma conta cadastrada. Adicione uma nova conta.
          </Typography>
        ) : (
          <List>
            {accounts.map((acc, idx) => (
              <ListItem
                key={idx}
                sx={{
                  ...WalletStyles.listItem,
                  backgroundColor: idx === activeIndex ? 'rgba(123, 162, 56, 0.1)' : 'transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
                onClick={() => handleSelectAccount(idx)}
              >
                <ListItemText
                  primary={acc.label}
                  secondary={idx === activeIndex ? '✅ Ativa' : 'Inativa'}
                  primaryTypographyProps={{ sx: { color: '#7BA238', fontWeight: idx === activeIndex ? 700 : 400 } }}
                  secondaryTypographyProps={{ sx: { color: 'rgba(123, 162, 56, 0.5)', fontSize: '0.75rem' } }}
                />
                <ListItemSecondaryAction>
                  <Button size="small" color="error" onClick={() => handleDeleteAccount(idx)}>
                    🗑️
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {decryptedKey && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(123, 162, 56, 0.05)', borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ color: 'rgba(123, 162, 56, 0.5)', fontSize: '0.7rem' }}>
              Chave da conta ativa (use nas credenciais):
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#7BA238', fontSize: '0.75rem', wordBreak: 'break-all', mb: 2 }}>
              {decryptedKey}
            </Typography>
            <Button 
              variant="contained" 
              sx={WalletStyles.actionButton}
              onClick={() => {
                try {
                  const privateKey = bsv.PrivateKey.fromHex(decryptedKey, homenetwork);
                  privateKey.compAdd(compState);
                  const fromAddress = bsv.Address.fromPrivateKey(privateKey).toString();
                  const message = encodeURIComponent(`Olá, criei minha carteira e gostaria de solicitar satoshis para realizar registros. Meu endereço é: ${fromAddress}`);
                  window.open(`https://wa.me/5511999999999?text=${message}`, '_blank');
                } catch (e) {
                  console.error('Invalid key for address generation', e);
                }
              }}
            >
              💬 Solicitar Fundos via WhatsApp
            </Button>
          </Box>
        )}
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#7BA238' }}>Adicionar Nova Conta</DialogTitle>
        <DialogContent>
          <TextField
            label="Label (Nome da Conta)"
            value={newLabel}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNewLabel(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{ ...WalletStyles.inputField, mt: 1, mb: 2 }}
          />
          <TextField
            label="Chave Privada ou Mnemônico"
            value={newKey}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNewKey(e.target.value)}
            fullWidth
            variant="outlined"
            type="password"
            multiline
            rows={2}
            sx={WalletStyles.inputField}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button onClick={handleAddAccount} disabled={saving} variant="contained" sx={WalletStyles.actionButton}>
            {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletAccounts;
